import { useEffect, useRef, useState } from 'react'
import { businessTimeZone } from '../lib/businessTime'
import { Link } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import apiClient from '../lib/api'
import { SocShell } from '../components/soc/SocShell'

// BRIEF-B10: the owner finishes the staff notice inside the portal, under the
// portal's own sign-in. Mounted at /client/staff-notice behind ProtectedRoute.
// The API-origin HTML page (GET /api/v1/day-one/staff-notice/page) could never
// confirm: its button read dw_token on the API origin, where it is empty.
//
// Routes (backend app/routers/day_one.py, read-only here), owner only:
//   GET  /api/v1/day-one/staff-notice
//   POST /api/v1/day-one/staff-notice  (no body)
//     200 → { notice, purge_clock, staff_told_at: string | null, watched }
//     404 → { detail: 'Not found' } for anyone but the business's owner
//
// Doctrine:
// - Nobody is watched who has not been told. Show the notice and the purge
//   clock exactly as the route returns them; the confirm posts only on the
//   owner's click, once.
// - A refusal carries a sentence in `detail`: show that sentence as written.
//   A refused GET (a plain member) shows that sentence and nothing else: no
//   heading, so the page does not reveal that a staff-notice step exists.
// - The confirmed state is the route's own: staff_told_at and watched.

type NoticeView = {
  notice: string
  purge_clock: string
  staff_told_at: string | null
  watched: boolean
}

const FALLBACK = 'That did not go through. Please try again in a moment.'

function sentenceFrom(err: unknown): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data
    ?.detail
  return typeof detail === 'string' && detail ? detail : FALLBACK
}

function isView(body: unknown): body is NoticeView {
  const b = body as NoticeView | null
  return !!b && typeof b.notice === 'string' && typeof b.purge_clock === 'string'
}

function toldOn(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short', timeZone: businessTimeZone() })
}

export function StaffNoticePage() {
  const [view, setView] = useState<NoticeView | null>(null)
  // A refused or failed read: no notice, no button.
  const [readRefusal, setReadRefusal] = useState<string | null>(null)
  // A refused or failed confirm: the notice stays, so the owner can try again.
  const [confirmRefusal, setConfirmRefusal] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const mountedRef = useRef(false)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    let live = true
    apiClient
      .get('/day-one/staff-notice')
      .then((res) => {
        if (!live) return
        if (isView(res.data)) setView(res.data)
        else setReadRefusal(FALLBACK)
      })
      .catch((err) => {
        if (live) setReadRefusal(sentenceFrom(err))
      })
    return () => {
      live = false
    }
  }, [attempt])

  function retry() {
    setReadRefusal(null)
    setView(null)
    setAttempt((n) => n + 1)
  }

  async function confirm() {
    if (sending) return
    setSending(true)
    setConfirmRefusal(null)
    try {
      const res = await apiClient.post('/day-one/staff-notice')
      if (!mountedRef.current) return
      if (isView(res.data)) setView(res.data)
      else setConfirmRefusal(FALLBACK)
    } catch (err) {
      if (mountedRef.current) setConfirmRefusal(sentenceFrom(err))
    }
    if (mountedRef.current) setSending(false)
  }

  const told = view?.staff_told_at ?? null

  return (
    // BRIEF-E5d: the one customer header. A sign-up step, before the first
    // meeting, so the menu is held back; "Go to your home" is the way on.
    <SocShell held mainClassName="flex flex-1 w-full items-start sm:items-center justify-center px-4 sm:px-6 py-8 box-border">
      <div className="max-w-md w-full min-w-0 bg-white rounded-2xl border border-gray-200 p-6 sm:p-8">
        {view === null && !readRefusal && (
          <p className="text-sm text-gray-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
            Loading…
          </p>
        )}

        {/* A read refusal paints the route's sentence only: no heading, no
            notice, no clock, no button. Only the network fallback may retry. */}
        {readRefusal && (
          <>
            <p role="alert" className="text-sm text-danger break-words">
              {readRefusal}
            </p>
            {readRefusal === FALLBACK && (
              <button
                type="button"
                onClick={retry}
                className="mt-3 text-sm font-medium text-navy underline"
              >
                Try again
              </button>
            )}
          </>
        )}

        {view && (
          <>
            <h1 className="text-xl font-serif font-bold text-navy">
              Before we watch: tell your staff
            </h1>
            <p className="text-sm text-gray-500 mt-2">
              Send your staff this note, in your own words if you like:
            </p>
            <blockquote
              className="mt-3 border-l-4 border-brass pl-3 text-sm text-navy whitespace-pre-wrap break-words"
              data-testid="staff-notice-text"
            >
              {view.notice}
            </blockquote>
            <p className="text-sm font-medium text-navy mt-4 break-words">
              {view.purge_clock}
            </p>

            {told ? (
              <div className="mt-6" role="status" data-testid="staff-notice-confirmed">
                <p className="text-sm text-navy break-words">
                  You confirmed you told your staff on {toldOn(told)}.
                </p>
                <p className="text-sm text-gray-500 mt-2 break-words">
                  {view.watched
                    ? 'Your business is being watched.'
                    : 'Watching starts once your day-one audit has qualified.'}
                </p>
                <Link
                  to="/client"
                  className="w-full inline-flex items-center justify-center mt-6 tap-min px-5 py-2.5 rounded-lg bg-brass text-white text-sm font-medium hover:bg-brass-dark transition-colors focus:outline-none focus:ring-2 focus:ring-brass focus:ring-offset-2"
                >
                  Go to your home
                </Link>
              </div>
            ) : (
              <>
                {confirmRefusal && (
                  <p role="alert" className="text-sm text-danger mt-4 break-words">
                    {confirmRefusal}
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => void confirm()}
                  disabled={sending}
                  className="w-full inline-flex items-center justify-center mt-6 tap-min px-5 py-2.5 rounded-lg bg-navy text-white text-sm font-medium disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-brass focus:ring-offset-2"
                >
                  {sending ? 'Confirming…' : 'I have told my staff'}
                </button>
              </>
            )}
          </>
        )}
      </div>
    </SocShell>
  )
}
